import { FC, useState, useRef } from 'react';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { Box, Modal, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Form } from 'react-router-dom';

import MandatoryFields from './components/MandatoryFields/MandatoryFields';
import OptionalFields from './components/OptionalFields/OptionalFields';
import DocumentsSection from './components/DocumentsSection/DocumentsSection';

import styles from './CreateActivityModal.module.scss';
import { CreateActivityFormData, ActivityType } from '../../../../../types/activity.types';
import { TripStop } from '../../../../../types/trip.types';
import { ACTIVITY_INITIAL_VALUES } from '../../../../../types/activity.initialValues';
import { getActivityUiFieldFromApiAttr, getFieldsForActivity } from '../../../../../types/activity.config';
import {
  useCreateTravelContactMutation,
  useCreateTripStopMutation,
  useDeleteTripStopMutation,
  useGetTripQuery,
  useGetTripStopQuery,
  useUpdateTravelContactMutation,
  useUpdateTripStopMutation,
} from '../../../../../store/services/tripsApi';
import { useAddStopDocumentMutation } from '../../../../../store/services/documentsApi';
import CustomSelect from '../../../../../common/CustomSelect';
import CustomButton from '../../../../../common/CustomButton';
import ConfirmActionModal from '../../../../../common/ConfirmActionModal/ConfirmActionModal';
import { useActivityPayload } from './hooks/useActivityPayload';
import { useActivityTypes } from './hooks/useActivityTypes';
import { useActivityToFormData } from './hooks/useActivityToFormData';
import { useAppSelector } from '../../../../../hooks/redux';
import { validateEndDateTimeAfterStart } from '../../../../../utils/dateUtils';
import { getAmountSpentValidationError } from '../../../../../utils/amountUtils';

type CreateActivityModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  tripId: number;
  editMode?: boolean;
  activityData?: TripStop;
  zIndex?: number;
};

const CreateActivityModal: FC<CreateActivityModalProps> = ({
  open,
  onClose,
  onSuccess,
  tripId,
  editMode = false,
  activityData,
  zIndex,
}) => {
  const modalSx = {
    ...(zIndex != null ? { zIndex } : {}),
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(34, 43, 58, 0.2)',
    },
  };
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [optionalAccordionOpen, setOptionalAccordionOpen] = useState(false);
  const [resErrors, setResErrors] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const isPremium = useAppSelector(state => state.user.is_premium);
  const [previousActivityType, setPreviousActivityType] = useState<ActivityType | ''>('');
  const { activityTypes, isLoading: isTypesLoading, hasTypes } = useActivityTypes();

  const formikRef = useRef<FormikProps<CreateActivityFormData>>(null);

  const [createTripStop] = useCreateTripStopMutation();
  const [updateTripStop] = useUpdateTripStopMutation();
  const [deleteTripStop, { isLoading: isDeletingStop }] = useDeleteTripStopMutation();
  const [createTravelContact] = useCreateTravelContactMutation();
  const [updateTravelContact] = useUpdateTravelContactMutation();
  const [addStopDocument] = useAddStopDocumentMutation();

  const { data: freshActivityData, isLoading: isLoadingActivity } = useGetTripStopQuery(activityData?.id || 0, {
    skip: !editMode || !activityData?.id,
  });
  const { data: tripDetails } = useGetTripQuery(String(tripId), { skip: !tripId });

  const { convertToApiPayload } = useActivityPayload(tripId);

  const initialFormData = useActivityToFormData(editMode && freshActivityData ? freshActivityData : activityData);

  const createDocumentForTripStop = async (values: CreateActivityFormData, tripStopId: number) => {
    if (values.documentName || values.documentTags.length > 0 || values.attachments.length > 0) {
      try {
        const formData = new FormData();
        formData.append('name', values.documentName || '');
        formData.append('trip_stop', String(tripStopId));

        values.documentTags.forEach(tag => {
          formData.append('tags', String(tag.id));
        });

        values.attachments.forEach(file => {
          formData.append('attachments', file);
        });

        const response = await addStopDocument(formData).unwrap();
        console.log('Document created successfully:', response);
      } catch (error) {
        console.error('Error creating document:', error);
      }
    }
  };

  const buildTravelContactPayload = (values: CreateActivityFormData, existingName?: string) => {
    const email = values.email?.trim();
    const phone = values.phone?.trim();
    const link = values.link?.trim();

    if (!email && !phone && !link) {
      return null;
    }

    const fallbackName =
      existingName?.trim() ||
      values.name?.trim() ||
      values.airline_details?.name ||
      (values.activityType ? `${values.activityType} Contact` : 'Travel Contact');

    return {
      name: fallbackName,
      ...(link ? { link } : {}),
      ...(email ? { email } : {}),
      ...(phone ? { phone_number: phone } : {}),
    };
  };

  const syncTravelContactForEdit = async (values: CreateActivityFormData, tripStopId: number) => {
    const currentStop = (freshActivityData as any) || (activityData as any);
    const existingContact = currentStop?.belonged_travel_contact || currentStop?.travel_contact;
    const contactPayload = buildTravelContactPayload(values, existingContact?.name);

    if (!contactPayload) {
      return;
    }

    const normalizedExisting = {
      email: (existingContact?.email || '').trim(),
      phone_number: (existingContact?.phone_number || '').trim(),
      link: (existingContact?.link || '').trim(),
    };
    const normalizedNext = {
      email: (contactPayload.email || '').trim(),
      phone_number: (contactPayload.phone_number || '').trim(),
      link: (contactPayload.link || '').trim(),
    };
    const isContactChanged =
      normalizedExisting.email !== normalizedNext.email ||
      normalizedExisting.phone_number !== normalizedNext.phone_number ||
      normalizedExisting.link !== normalizedNext.link;

    if (existingContact?.id && !isContactChanged) {
      return;
    }

    if (existingContact?.id) {
      await updateTravelContact({
        tripStopId: String(tripStopId),
        contactId: String(existingContact.id),
        contactData: contactPayload,
      }).unwrap();
      return;
    }

    await createTravelContact({
      tripStopId: String(tripStopId),
      contactData: contactPayload,
    }).unwrap();
  };

  const handleSubmit = async (
    values: CreateActivityFormData,
    { setFieldError, setFieldTouched }: FormikHelpers<CreateActivityFormData>
  ) => {
    try {
      setIsLoading(true);
      setResErrors(null);

      if (editMode && activityData) {
        const payload = convertToApiPayload(values);
        const { travel_contact, ...activityDataWithoutTravelContact } = payload.activityData as Record<string, unknown>;
        console.log('Updating trip stop with payload:', payload);

        await updateTripStop({
          id: activityData.id,
          tripId: tripId.toString(),
          activityType: values.activityType,
          data: activityDataWithoutTravelContact as any,
        }).unwrap();

        await syncTravelContactForEdit(values, activityData.id);
        console.log('Trip stop updated successfully');
        await createDocumentForTripStop(values, activityData.id);

        setIsSuccess(true);
        onSuccess?.();
        handleClose();
      } else {
        const payload = convertToApiPayload(values);
        console.log('Creating trip stop with payload:', payload);

        const response = await createTripStop(payload).unwrap();
        console.log('Trip stop created successfully:', response);

        if (response.id) {
          await createDocumentForTripStop(values, response.id);
        }

        setIsSuccess(true);
        onSuccess?.();
        handleClose();
      }
    } catch (error: any) {
      console.error('Error creating/updating trip stop:', error);

      const rawErrors = error?.data?.errors;
      if (Array.isArray(rawErrors)) {
        const fieldErrors: Record<string, string> = {};
        const general: string[] = [];

        for (const err of rawErrors) {
          const detail = err.detail?.trim();
          if (!detail) continue;

          const apiAttr = err.attr;
          if (!apiAttr) {
            general.push(detail);
            continue;
          }

          const uiKey = getActivityUiFieldFromApiAttr(apiAttr);
          if (uiKey && uiKey in ACTIVITY_INITIAL_VALUES) {
            setFieldError(uiKey as keyof CreateActivityFormData, detail);
            setFieldTouched(uiKey as keyof CreateActivityFormData, true, false);
            fieldErrors[uiKey] = fieldErrors[uiKey] ? `${fieldErrors[uiKey]} ${detail}` : detail;
          } else {
            general.push(detail);
          }
        }

        const optionalFields = new Set(getFieldsForActivity(values.activityType).optional);
        if (Object.keys(fieldErrors).some(key => optionalFields.has(key))) {
          setOptionalAccordionOpen(true);
        }

        if (general.length > 0) {
          setResErrors(
            general.length > 1 ? general.map((msg, index) => `${index + 1}. ${msg}`).join('\n') : general[0]
          );
        } else if (Object.keys(fieldErrors).length === 0) {
          setResErrors('Validation error occurred. Please check your input.');
        } else {
          setResErrors(null);
        }
      } else if (error?.data?.message) {
        setResErrors(error.data.message);
      } else if (error?.data?.detail) {
        setResErrors(error.data.detail);
      } else if (error?.message) {
        setResErrors(error.message);
      } else {
        setResErrors(`Failed to ${editMode ? 'update' : 'create'} activity. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formikValidate = (values: CreateActivityFormData) => {
    const errors: Partial<Record<keyof CreateActivityFormData, string>> = {};

    if (!values.activityType) {
      errors.activityType = 'Activity type is required';
    }

    if (values.activityType) {
      switch (values.activityType) {
        case 'Flight':
          if (!values.flightFrom) errors.flightFrom = 'Flight from is required';
          if (!values.flightTo) errors.flightTo = 'Flight to is required';
          if (!values.departureDate) errors.departureDate = 'Departure date is required';
          if (!values.departureTime) errors.departureTime = 'Departure time is required';
          if (!values.arrivalDate) errors.arrivalDate = 'Arrival date is required';
          if (!values.arrivalTime) errors.arrivalTime = 'Arrival time is required';
          break;

        case 'Hotel':
          if (!values.name) errors.name = 'Hotel name is required';
          if (!values.checkInDate) errors.checkInDate = 'Check-in date is required';
          if (!values.checkInTime) errors.checkInTime = 'Check-in time is required';
          if (!values.checkOutDate) errors.checkOutDate = 'Check-out date is required';
          if (!values.checkOutTime) errors.checkOutTime = 'Check-out time is required';
          break;

        case 'BorderCrossing':
          if (!values.date) errors.date = 'Date is required';
          if (!values.time) errors.time = 'Time is required';
          if (!values.country_from) errors.country_from = 'Country from is required';
          if (!values.country_to) errors.country_to = 'Country to is required';
          break;

        case 'Transportation':
          if (!values.vehicleType) errors.vehicleType = 'Vehicle type is required';
          if (!values.departureDate) errors.departureDate = 'Departure date is required';
          if (!values.departureTime) errors.departureTime = 'Departure time is required';
          if (!values.arrivalDate) errors.arrivalDate = 'Arrival date is required';
          if (!values.arrivalTime) errors.arrivalTime = 'Arrival time is required';
          break;

        case 'Parking':
        case 'Directions':
          if (!values.place) errors.place = 'Place is required';
          if (!values.startDate) errors.startDate = 'Start date is required';
          if (!values.startTime) errors.startTime = 'Start time is required';
          if (!values.endDate) errors.endDate = 'End date is required';
          if (!values.endTime) errors.endTime = 'End time is required';
          break;

        case 'Rail':
        case 'Cruise':
        case 'Ferry':
          if (!values.name) errors.name = 'Name is required';
          if (!values.departureDate) errors.departureDate = 'Departure date is required';
          if (!values.departureTime) errors.departureTime = 'Departure time is required';
          if (!values.arrivalDate) errors.arrivalDate = 'Arrival date is required';
          if (!values.arrivalTime) errors.arrivalTime = 'Arrival time is required';
          break;

        default:
          if (!values.name && values.activityType !== 'Shopping') {
            errors.name = 'Name is required';
          }
          if (!values.startDate) errors.startDate = 'Start date is required';
          if (!values.startTime) errors.startTime = 'Start time is required';
          if (!values.endDate) errors.endDate = 'End date is required';
          if (!values.endTime) errors.endTime = 'End time is required';
      }
    }

    if (values.email && values.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(values.email)) {
        errors.email = 'Invalid email format';
      }
    }

    if (values.phone && values.phone.trim() !== '') {
      const cleaned = values.phone.replace(/[\s\-()]/g, '');
      const phoneRegex = /^\+?[1-9]\d{9,14}$/;

      if (!phoneRegex.test(cleaned)) {
        errors.phone = 'Invalid phone number format (must contain 10-15 digits)';
      }
    }

    if (values.link && values.link.trim() !== '') {
      const urlRegex = /^https?:\/\/[^\s]+$/;
      if (!urlRegex.test(values.link)) {
        errors.link = 'Link must start with http:// or https://';
      }
    }

    const amountSpentError = getAmountSpentValidationError(values.amountSpent);
    if (amountSpentError) {
      errors.amountSpent = amountSpentError;
    }

    const applyRangeErrors = (
      startDate?: string,
      startTime?: string,
      endDate?: string,
      endTime?: string,
      endDateKey: keyof CreateActivityFormData = 'endDate',
      endTimeKey: keyof CreateActivityFormData = 'endTime'
    ) => {
      const rangeErrors = validateEndDateTimeAfterStart(startDate, startTime, endDate, endTime);
      if (rangeErrors.endDate && !errors[endDateKey]) {
        errors[endDateKey] = rangeErrors.endDate;
      }
      if (rangeErrors.endTime && !errors[endTimeKey]) {
        errors[endTimeKey] = rangeErrors.endTime;
      }
    };

    applyRangeErrors(values.startDate, values.startTime, values.endDate, values.endTime);
    applyRangeErrors(
      values.checkInDate,
      values.checkInTime,
      values.checkOutDate,
      values.checkOutTime,
      'checkOutDate',
      'checkOutTime'
    );
    applyRangeErrors(
      values.departureDate,
      values.departureTime,
      values.arrivalDate,
      values.arrivalTime,
      'arrivalDate',
      'arrivalTime'
    );

    return errors;
  };

  const handleClose = () => {
    onClose();
    setResErrors(null);
    setIsSuccess(false);
    setShowDeleteConfirm(false);
    setPreviousActivityType('');
    setOptionalAccordionOpen(false);
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  const handleConfirmDeleteActivity = async () => {
    if (!editMode || !activityData?.id) return;

    try {
      await deleteTripStop({ id: activityData.id, tripId: tripId.toString() }).unwrap();
      setShowDeleteConfirm(false);
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error deleting trip stop:', error);
      setResErrors('Failed to delete activity. Please try again.');
      setShowDeleteConfirm(false);
    }
  };

  const handleActivityTypeChange = (event: any, formik: FormikProps<CreateActivityFormData>) => {
    const newActivityType = event.target.value as ActivityType;

    if (editMode) {
      return;
    }

    if (newActivityType !== previousActivityType && previousActivityType !== '') {
      const newValues = {
        ...ACTIVITY_INITIAL_VALUES,
        activityType: newActivityType,
      };
      formik.resetForm({ values: newValues });
    } else {
      formik.handleChange(event);
    }

    setPreviousActivityType(newActivityType);
  };

  if ((isTypesLoading && !hasTypes) || (editMode && isLoadingActivity)) {
    return (
      <Modal
        open={open}
        onClose={handleClose}
        sx={modalSx}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: '20px',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">Loading...</Typography>
        </Box>
      </Modal>
    );
  }

  return (
    <>
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="create-activity-modal"
      sx={modalSx}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '600px',
          minWidth: 375,
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: '20px',
          p: '32px 0',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          boxShadow: 'none',
          maxHeight: 'min(90vh, 90dvh)',
          '@media (max-width: 900px)': {
            p: '16px 0',
            top: 'auto',
            bottom: 0,
            left: 0,
            right: 0,
            transform: 'none',
            maxWidth: '100%',
            minWidth: 0,
            width: '100%',
            borderRadius: '20px 20px 0 0',
            maxHeight: 'min(92vh, 92dvh)',
          },
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: 'pointer' }}>
          <CloseIcon
            sx={{ width: '30px', height: '30px' }}
            onClick={handleClose}
          />
        </Box>

        <Box className={styles.modalContent}>
          <Typography
            variant="h5Bold"
            sx={{ textAlign: 'center', mb: 3, color: '#222B3A' }}
          >
            {editMode ? 'Edit Stop' : 'Add New Stop'}
          </Typography>

          <Formik
            innerRef={formikRef}
            initialValues={editMode ? initialFormData : ACTIVITY_INITIAL_VALUES}
            validate={formikValidate}
            onSubmit={handleSubmit}
            enableReinitialize={editMode}
          >
            {formik => {
              const { values, handleSubmit, errors, touched } = formik;

              return (
                <Form
                  className={styles.activityForm}
                  onSubmit={handleSubmit}
                >
                  <Accordion
                    defaultExpanded
                    className={styles.accordion}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography
                        variant="h9Bold"
                        sx={{ color: '#222B3A' }}
                      >
                        Mandatory
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box className={styles.formSection}>
                        <CustomSelect
                          name="activityType"
                          inputLabel="Activity Type"
                          value={values.activityType || ''}
                          handleChange={e => handleActivityTypeChange(e, formik)}
                          data={activityTypes}
                          disabled={editMode}
                          error={touched.activityType && errors.activityType ? String(errors.activityType) : ''}
                        />

                        {values.activityType && (
                          <MandatoryFields
                            formik={formik}
                            tripMinDate={tripDetails?.start_date?.split('T')[0]}
                            tripMaxDate={tripDetails?.end_date?.split('T')[0]}
                          />
                        )}
                        {isPremium && values.activityType === 'Flight' && (
                          <Box className={styles.flightNotice}>
                            <InfoOutlinedIcon className={styles.flightNoticeIcon} />
                            <Typography
                              variant="h11"
                              className={styles.flightNoticeText}
                            >
                              To receive flight status notifications (e.g. delays, gate changes, cancellations), please
                              fill in the <strong>Airline</strong> and <strong>Flight Number</strong> fields in the
                              Optional section.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    className={styles.accordion}
                    expanded={optionalAccordionOpen}
                    onChange={(_, expanded) => setOptionalAccordionOpen(expanded)}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography
                        variant="h9Bold"
                        sx={{ color: '#222B3A' }}
                      >
                        Optional
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box className={styles.formSection}>
                        {values.activityType && <OptionalFields formik={formik} />}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion className={styles.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography
                        variant="h9Bold"
                        sx={{ color: '#222B3A' }}
                      >
                        Documents
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box className={styles.formSection}>
                        <DocumentsSection formik={formik} />
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Box className={styles.modalActions}>
                    {editMode ? (
                      <CustomButton
                        btnType="button"
                        variant="outlined"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={isLoading || isDeletingStop}
                        customClass={styles.deleteActivityButton}
                      >
                        Delete Activity
                      </CustomButton>
                    ) : (
                      <CustomButton
                        btnType="button"
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isLoading}
                      >
                        Cancel
                      </CustomButton>
                    )}
                    <CustomButton
                      btnType="submit"
                      variant="contained"
                      disabled={isLoading || isDeletingStop || !values.activityType}
                    >
                      {isLoading
                        ? editMode
                          ? 'Saving...'
                          : 'Creating...'
                        : 'Save Changes'}
                    </CustomButton>
                  </Box>

                  {isSuccess && (
                    <Typography
                      variant="h12"
                      sx={{ color: '#208B64', textAlign: 'center', mt: 2 }}
                    >
                      Activity {editMode ? 'updated' : 'created'} successfully!
                    </Typography>
                  )}

                  {resErrors && (
                    <Typography
                      variant="h12"
                      sx={{
                        color: '#C8102E',
                        textAlign: 'center',
                        mt: 2,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {resErrors}
                    </Typography>
                  )}
                </Form>
              );
            }}
          </Formik>
        </Box>
      </Box>
    </Modal>
    <ConfirmActionModal
      open={showDeleteConfirm}
      onClose={() => !isDeletingStop && setShowDeleteConfirm(false)}
      onConfirm={handleConfirmDeleteActivity}
      description="Do you really want to delete this activity?"
      confirmLabel="Delete Activity"
      isConfirming={isDeletingStop}
      zIndex={zIndex != null ? zIndex + 1 : undefined}
    />
    </>
  );
};

export default CreateActivityModal;
